import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Printer,
  Edit3,
  ArrowLeft,
  Loader2,
  Package,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  Truck,
  Building,
  Calendar,
  User,
  Clock,
  FileText,
  Activity,
  ExternalLink,
  ShieldCheck,
  CreditCard
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from './ViewManufacturerClearance.module.css';
import NotFound from '../Error/NotFound';

import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';

const ViewManufacturerClearance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [clearance, setClearance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchClearance = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/manufacturer-clearances/${id}`);
      if (response.data.status === 'success') {
        setClearance(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching clearance:', error);
      toast.error('Failed to fetch clearance details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClearance();
  }, [id]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  if (loading) {
    return (
      <div className={styles['view-po-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={toggleSidebar} />
          <div className={styles['loading-container']}>
            <Loader2 className={styles['spinner']} size={40} />
            <p>Loading Clearance Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!clearance) {
    return <NotFound />;
  }


  const payment = clearance.vendor_payments && clearance.vendor_payments.length > 0 ? clearance.vendor_payments[0] : null;

  return (
    <div className={styles['view-po-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Clearance Detail</h1>
              <p>Review manufacturer clearance and associated payment record</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={18} />
                <span>Print</span>
              </button>
              <button className={styles['btn-secondary']} onClick={() => navigate(-1)}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => navigate(`/manufacturer-clearance/edit/${clearance.id}`)}>
                <Edit3 size={18} />
                <span>Edit Record</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['po-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>CLR-{String(clearance.id).padStart(4, '0')}</h1>
                <span className={`${styles['status-badge']} ${clearance.is_advance ? styles['pending'] : styles['completed']}`}>
                  {clearance.is_advance ? 'ADVANCE CLEARANCE' : 'REGULAR CLEARANCE'}
                </span>
              </div>
            </div>

            <div className={styles['po-meta']}>
              <div className={styles['meta-item']}>
                <Building size={16} />
                <span>Project: <strong>{clearance.project?.project_name}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <User size={16} />
                <span>Vendor: <strong>{clearance.vendor?.vendor_name}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Calendar size={16} />
                <span>Clearance Date: <strong>{new Date(clearance.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><IndianRupee size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Clearance Amount</label>
                <h2>₹{parseFloat(clearance.amount).toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-paid']}`}><CheckCircle2 size={20} /></div>
                <span className={styles['m-trend']} style={{ color: '#10b981' }}>Verified</span>
              </div>
              <div className={styles['m-info']}>
                <label>Payment Mode</label>
                <h2 style={{ color: '#16a34a' }}>
                  {payment?.payment_mode === 1 ? 'Cash' : 
                   payment?.payment_mode === 2 ? 'Bank Transfer' :
                   payment?.payment_mode === 3 ? 'Cheque' :
                   payment?.payment_mode === 4 ? 'UPI' : 'N/A'}
                </h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-balance']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Advance Status</label>
                <h2 style={{ color: clearance.is_advance ? '#f59e0b' : '#2563eb' }}>
                  {clearance.is_advance ? 'Yes (Advance)' : 'No (Regular)'}
                </h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-tax']}`}><FileText size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Linked PO</label>
                <h2>{clearance.purchase_order?.po_number || 'N/A'}</h2>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Payment Info Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><CreditCard size={20} /></div>
                  <h3>Associated Payment Details</h3>
                </div>
                {payment ? (
                  <div className={styles['info-table']} style={{ padding: '0 20px 20px' }}>
                    <div className={styles['s-row']}><span>Reference Number</span><span>{payment.ref_transaction_number || 'N/A'}</span></div>
                    {payment.cheque_date && (
                      <div className={styles['s-row']}><span>Cheque Date</span><span>{payment.cheque_date}</span></div>
                    )}
                    <div className={styles['s-row']}><span>Bank Account</span><span>{payment.bank_account?.bank_name || 'N/A'}</span></div>
                    <div className={styles['s-row']}><span>Remarks</span><span>{payment.remarks || 'N/A'}</span></div>
                  </div>
                ) : (
                  <div className={styles['not-found-container']} style={{ minHeight: 'auto', padding: '40px' }}>
                    <p>No associated payment record found.</p>
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><FileText size={18} /></div>
                  <h3>Clearance Notes</h3>
                </div>
                <p className={styles['note-text']}>{clearance.note || 'No additional notes provided.'}</p>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Stakeholders Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Building size={20} /></div>
                  <h3>Stakeholders</h3>
                </div>
                <div className={styles['stakeholder-list']}>
                  <div className={styles['sh-item']} onClick={() => navigate(`/vendors/view/${clearance.vendor_id}`)} style={{ cursor: 'pointer' }}>
                    <div className={styles['sh-avatar']}>V</div>
                    <div className={styles['sh-info']}>
                      <h4>{clearance.vendor?.vendor_name}</h4>
                      <p>Manufacturer Vendor</p>
                    </div>
                  </div>
                  <div className={styles['sh-item']} onClick={() => navigate(`/projects/${clearance.project_id}`)} style={{ cursor: 'pointer' }}>
                    <div className={styles['sh-avatar']} style={{ background: '#fff7ed', color: '#f97316' }}>P</div>
                    <div className={styles['sh-info']}>
                      <h4>{clearance.project?.project_name}</h4>
                      <p>Linked Project</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase Order Context */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Package size={20} /></div>
                  <h3>Purchase Order Link</h3>
                </div>
                <div className={styles['info-table']}>
                  <div className={styles['s-row']}><span>PO Number</span><span className={styles['bold']}>{clearance.purchase_order?.po_number}</span></div>
                  <div className={styles['s-row']}><span>PO Date</span><span>{clearance.purchase_order?.po_date ? new Date(clearance.purchase_order.po_date).toLocaleDateString() : 'N/A'}</span></div>
                  <div className={styles['s-row']}><span>PO Amount</span><span>₹{parseFloat(clearance.purchase_order?.total_amount || 0).toLocaleString()}</span></div>
                  <div className={styles['s-row']} style={{ borderBottom: 'none' }}>
                    <button 
                      className={styles['btn-secondary']} 
                      style={{ width: '100%', marginTop: '10px' }}
                      onClick={() => navigate(`/purchase-orders/${clearance.purchase_order_id}`)}
                    >
                      <ExternalLink size={16} />
                      <span>View Purchase Order</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Timeline/Meta */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Clock size={20} /></div>
                  <h3>Record Meta</h3>
                </div>
                <div className={styles['timeline-list']}>
                  <div className={styles['timeline-item']}>
                    <div className={styles['tl-icon']} style={{ background: '#f0fdf4', color: '#16a34a' }}>
                      <Activity size={16} />
                    </div>
                    <div className={styles['tl-content']}>
                      <h4>Record Created</h4>
                      <p>{new Date(clearance.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  {clearance.is_advance && (
                    <div className={styles['timeline-item']}>
                      <div className={styles['tl-icon']} style={{ background: '#fffbeb', color: '#f59e0b' }}>
                        <Calendar size={16} />
                      </div>
                      <div className={styles['tl-content']}>
                        <h4>Advance Date</h4>
                        <p>{new Date(clearance.advance_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default ViewManufacturerClearance;
